import axios from "axios";
import { devLog, environment } from "../config/environment";
import type {
  ChamadoDetalhes,
  ChamadoKanban,
  LoginRequest,
  LoginResponse,
  SeveridadeFiltro,
  CategoriaFiltro,
  StatusFiltro,
  FiltrosAPI,
} from "../types/api";
import { clearAuthSession, getAuthToken } from "../stores/authStore";
import { paginaDeEntrada } from "../features/equipe/sessao";

// Configuração da API baseada no ambiente
const API_BASE_URL = environment.apiBaseUrl;

// Log da configuração da API em desenvolvimento
devLog("Configurando API:", {
  environment: environment.name,
  baseURL: API_BASE_URL,
  isDevelopment: environment.isDevelopment,
});

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de resposta para lidar com 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Erros dos endpoints de autenticação são tratados pela própria tela.
    if (error.config?.url?.includes("/auth/")) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // 401 encerra a sessão. Antes, a mera presença das chaves no localStorage
      // era tratada como prova de validade e o erro era devolvido sem limpar
      // nada, deixando o usuário numa tela autenticada que não carregava dados.
      clearAuthSession();

      // A equipe volta para a própria entrada (conta Microsoft), não para o
      // login de usuário e senha.
      const entrada = paginaDeEntrada();
      if (window.location.pathname !== entrada) {
        window.location.href = entrada;
      }
    }

    return Promise.reject(error);
  }
);

// Serviços da API
export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  },
};

export const chamadosService = {
  getKanban: async (filtros?: FiltrosAPI): Promise<ChamadoKanban[]> => {
    const params = new URLSearchParams();

    console.log("Filtros recebidos no serviço:", filtros);

    if (filtros?.severidade !== undefined && filtros.severidade.length > 0)
      params.append("severidade", filtros.severidade.join(","));
    if (filtros?.categoria !== undefined)
      params.append("categoria", filtros.categoria.toString());
    if (filtros?.status !== undefined && filtros.status.length > 0)
      params.append("status", filtros.status.join(","));
    if (filtros?.mes) params.append("mes", filtros.mes.toString());
    if (filtros?.ano) params.append("ano", filtros.ano.toString());

    const queryString = params.toString();
    const url = queryString
      ? `/chamados/kanban?${queryString}`
      : "/chamados/kanban";

    console.log("URL da API:", url);

    const response = await api.get(url);
    return response.data;
  },

  getById: async (id: number): Promise<ChamadoDetalhes> => {
    const response = await api.get(`/chamados/${id}`);
    return response.data;
  },

  updatePrioridade: async (
    id: number,
    prioridade: number | null,
  ): Promise<{ message: string; prioridade: number | null }> => {
    const response = await api.patch(`/chamados/${id}/prioridade`, {
      prioridade,
    });
    return response.data;
  },

  // Serviços para filtros
  getSeveridades: async (): Promise<SeveridadeFiltro[]> => {
    const response = await api.get("/chamados/filtros/severidades");
    return response.data;
  },

  getCategorias: async (): Promise<CategoriaFiltro[]> => {
    const response = await api.get("/chamados/filtros/categorias");
    return response.data;
  },

  getStatus: async (): Promise<StatusFiltro[]> => {
    const response = await api.get("/chamados/filtros/status");
    return response.data;
  },
};
